import moment from 'moment';

import {
  CLIENT_ADDRESS_FIELD_NAMES,
  GENERAL_INFO_FIELD_NAMES,
  GENERAL_INITIAL_VALUES,
  PROVIDER_REQUIRED_ADDRESS_FIELD_NAMES,
  PROVIDER_TO_CLIENT_ADDRESS_FIELDS,
} from './constants';
import { GENERAL_INFO_FORM_SCHEMA } from '../validationSchema';
import { API_DATE_FORMAT } from '../../../../../../../constants';

import type {
  GeneralInformationFieldName,
  GeneralInformationFieldOverride,
  GeneralInformationFieldValue,
  GeneralInformationPatchData,
  GeneralInformationSavedFieldValue,
  GeneralInformationSavedFieldValues,
} from './types';
import type { GeneralFormValues } from '../../../../../common';
import type { IOnboardingQuestionnaireGeneralInfo } from '../types';
import type { IProvider } from '../../../../../../../utils/types';

export function getGeneralInformationInitialValues(
  generalInfo: IOnboardingQuestionnaireGeneralInfo | null | undefined,
): GeneralFormValues {
  if (generalInfo == null) {
    return GENERAL_INITIAL_VALUES;
  }

  return {
    provider_full_name: generalInfo.provider_full_name ?? '',
    provider_email: generalInfo.provider_email ?? '',
    primary_language: generalInfo.primary_language ?? '',
    relationship_to_client: generalInfo.relationship_to_client ?? '',
    client_first_name: generalInfo.client_first_name ?? '',
    client_middle_name: generalInfo.client_middle_name ?? '',
    client_last_name: generalInfo.client_last_name ?? '',
    client_date_of_birth: generalInfo.client_date_of_birth
      ? moment(generalInfo.client_date_of_birth)
      : null,
    client_sex: generalInfo.client_sex ?? '',
    client_address_same_as_provider: generalInfo.client_address_same_as_provider ?? false,
    client_street_address_line_one: generalInfo.client_street_address_line_one ?? '',
    client_street_address_line_two: generalInfo.client_street_address_line_two ?? '',
    client_street_address_zip: generalInfo.client_street_address_zip ?? '',
    client_street_address_city: generalInfo.client_street_address_city ?? '',
    client_street_address_state: generalInfo.client_street_address_state ?? '',
  };
}

export function getSavedFieldValues(values: GeneralFormValues): GeneralInformationSavedFieldValues {
  return GENERAL_INFO_FIELD_NAMES.reduce<GeneralInformationSavedFieldValues>(
    (savedValues, fieldName) => ({
      ...savedValues,
      [fieldName]: getSavedFieldValue(fieldName, values),
    }),
    {},
  );
}

export function getSavedFieldValue(
  name: GeneralInformationFieldName,
  values: GeneralFormValues,
): GeneralInformationSavedFieldValue {
  if (name === 'client_date_of_birth') {
    return values.client_date_of_birth?.format(API_DATE_FORMAT) ?? '';
  }

  if (name === 'client_sex') {
    return values.client_sex.toLowerCase();
  }

  const fieldValue = values[name];
  return fieldValue === true || fieldValue === false
    ? fieldValue
    : fieldValue == null
      ? ''
      : String(fieldValue);
}

export function getChangedSavedFieldValues(
  values: GeneralFormValues,
  savedValues: GeneralInformationSavedFieldValues,
): GeneralInformationSavedFieldValues {
  return GENERAL_INFO_FIELD_NAMES.reduce<GeneralInformationSavedFieldValues>(
    (changed, fieldName) => {
      const savedValue = getSavedFieldValue(fieldName, values);
      if (savedValues[fieldName] === savedValue) {
        return changed;
      }
      return { ...changed, [fieldName]: savedValue };
    },
    {},
  );
}

export function getChangedGeneralInfoFieldNames(
  changedSavedValues: GeneralInformationSavedFieldValues,
): GeneralInformationFieldName[] {
  return GENERAL_INFO_FIELD_NAMES.filter((fieldName) => fieldName in changedSavedValues);
}

export function applyGeneralInfoFieldOverrides(
  values: GeneralFormValues,
  overrides: GeneralInformationFieldOverride[],
): GeneralFormValues {
  return overrides.reduce<GeneralFormValues>(
    (acc, { name, value }) => ({ ...acc, [name]: value }),
    values,
  );
}

export function getMergedPatchData(
  values: GeneralFormValues,
  names: GeneralInformationFieldName[],
): GeneralInformationPatchData {
  return names.reduce<GeneralInformationPatchData>(
    (data, name) => ({ ...data, ...getPatchData(name, values) }),
    {},
  );
}

export function isClientAddressFieldName(
  name: GeneralInformationFieldName,
): name is (typeof CLIENT_ADDRESS_FIELD_NAMES)[number] {
  return CLIENT_ADDRESS_FIELD_NAMES.some((fieldName) => fieldName === name);
}

export function hasProviderAddressFields(provider: IProvider): boolean {
  return PROVIDER_REQUIRED_ADDRESS_FIELD_NAMES.every((fieldName) => Boolean(provider[fieldName]));
}

export function getClientAddressSyncOverridesFromProvider(
  provider: IProvider,
): GeneralInformationFieldOverride[] {
  return PROVIDER_TO_CLIENT_ADDRESS_FIELDS.map(([providerFieldName, clientFieldName]) => ({
    name: clientFieldName,
    value:
      providerFieldName === 'street_address_zip'
        ? String(provider.street_address_zip ?? '')
        : providerFieldName === 'street_address_state'
          ? (provider.street_address_state ?? '').toUpperCase()
          : (provider[providerFieldName] ?? ''),
  }));
}

export function getPatchData(
  name: GeneralInformationFieldName,
  values: GeneralFormValues,
): GeneralInformationPatchData {
  const data: GeneralInformationPatchData = {};

  if (name === 'client_date_of_birth') {
    data.client_date_of_birth = values.client_date_of_birth?.format(API_DATE_FORMAT) ?? '';
    return data;
  }
  if (name === 'client_sex') {
    data.client_sex = values.client_sex.toLowerCase();
    return data;
  }
  if (name === 'client_address_same_as_provider') {
    data.client_address_same_as_provider = values.client_address_same_as_provider;
    return data;
  }

  const fieldValue = values[name];
  data[name] = fieldValue == null ? '' : String(fieldValue);
  return data;
}

export function getValuesWithFieldOverride(
  values: GeneralFormValues,
  name: GeneralInformationFieldName,
  value: GeneralInformationFieldValue | undefined,
): GeneralFormValues {
  if (value === undefined) {
    return values;
  }

  return { ...values, [name]: value };
}

export function isDateFieldValid(
  name: GeneralInformationFieldName,
  values: GeneralFormValues,
): boolean {
  if (name !== 'client_date_of_birth') {
    return true;
  }

  return values.client_date_of_birth == null || values.client_date_of_birth.isValid();
}

export async function isFieldSchemaValid(
  name: GeneralInformationFieldName,
  values: GeneralFormValues,
): Promise<boolean> {
  try {
    await GENERAL_INFO_FORM_SCHEMA.validateAt(name, values);
    return true;
  } catch {
    return false;
  }
}
